import React, { useEffect, useState } from "react";
import { SwitchTransition, CSSTransition } from "react-transition-group";
import { type AuthUser } from "wasp/auth";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import Fade from "@mui/material/Fade";
import { useNavigate } from "react-router-dom";
import {
  createUserSubscription,
  getAllPlans,
  getUserSubscriptions,
  initializeUserData,
  updateCurrentUser,
  useQuery,
} from "wasp/client/operations";

// Components
import PlaceSelectionView from "./PlaceSelectionView";
import SizeSelectionView from "./SizeSelectionView";
import ExistingProviderSelectionView from "./ExistingProviderSelectionView";
import PlanSelectionView from "./PlanSelectionView";
import WelcomeView from "./WelcomeView";
import PaymentView from "./PaymentView";
import { setCurrentUser } from "../../store/userSlice";
import { useDispatch } from "react-redux";
// Styles
import styles from "./index.module.scss";
import "./index.css";
import { analytics } from "../../analytics";
import PricingPage from "../../payment/PricingPage";
import { useTranslation } from "../../i18n/hooks";

type OnboardingView = {
  label: string;
  title: string;
  description: string;
  component: React.ComponentType<any>;
  skippable: boolean;
  canEndOnboarding: boolean;
  next: string | null;
  prev: string | null;
  isLast: boolean;
  windowWidth: number;
  componentProps?: Record<string, any>;
  containerClasses?: string;
};

type OnboardingConfig = {
  views: OnboardingView[];
};

const OnboardingPage = ({ user }: { user: AuthUser }) => {
  const { t } = useTranslation();
  const [selectedPlaces, setSelectedPlaces] = useState<string[]>([]);
  const [selectedSize, setSelectedSize] = useState<string>("");
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const [currentStep, setCurrentStep] = useState<number>(
    parseInt(localStorage.getItem('currentStep') || '0', 10)
  );
  
  const onboardingConfig: OnboardingConfig = {
    views: [
      {
        label: "Welcome",
        title: "",
        description: "",
        component: WelcomeView,
        componentProps: {},
        canEndOnboarding: false,
        skippable: false,
        next: "ServiceSelection",
        prev: null,
        isLast: false,
        windowWidth: 796,
      },
      {
        label: "ServiceSelection",
        title: t('onboarding.maximize_performance'),
        description: t('onboarding.choose_tools'),
        component: PlaceSelectionView,
        componentProps: {
          selectedPlaces: [],
          setSelectedPlaces: (places: string[]) => {},
        },
        canEndOnboarding: true,
        skippable: false,
        next: "SizeSelection",
        prev: null,
        isLast: false,
        windowWidth: 850,
      },
      {
        label: "ProductEntryMethodSelection",
        title: t('onboarding.enter_product_data'),
        description: t('onboarding.select_import_method'),
        component: ExistingProviderSelectionView,
        canEndOnboarding: false,
        skippable: false,
        next: "SizeSelection",
        prev: "ServiceSelection",
        isLast: false,
        windowWidth: 850,
      },
      {
        label: "SizeSelection",
        title: t('onboarding.orders_per_month'),
        description: t('onboarding.tailor_features'),
        component: SizeSelectionView,
        canEndOnboarding: false,
        skippable: false,
        next: null,
        prev: "ProductEntryMethodSelection",
        isLast: true,
        windowWidth: 800,
      },
    ],
  };
  
  const [canEndOnboarding, setCanEndOnboarding] = useState(
    onboardingConfig.views[currentStep]?.canEndOnboarding
  );
  const [direction, setDirection] = useState("forward");
  const navigate = useNavigate();
  const [currentWidth, setCurrentWidth] = useState(
    `${onboardingConfig.views[currentStep]?.windowWidth}px`
  );
  const widthChangeDelay = 300;
  const [shouldRenderOnboarding, setShouldRenderOnboarding] = useState(false);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setCurrentUser(user));
  }, [user])

  useEffect(() => {
    analytics.sendOnboardingStepViewedEvent({
      step: currentStep,
      stepName: onboardingConfig.views[currentStep]?.label,
      email: user.email ?? user.id,
    });
  }, [currentStep])

  const {
    data: plans,
    isLoading: plansLoading,
    error: plansError,
  } = useQuery(getAllPlans);
  const { data: subscriptions, isLoading: subsLoading } = useQuery(
    getUserSubscriptions,
    { userId: user.id }
  );
  
    useEffect(() => {
    const savedParams = localStorage.getItem('urlParams');
    const chargeId = savedParams ? JSON.parse(savedParams)?.charge_id : null;
    if (chargeId) {
      localStorage.removeItem('urlParams');
    }
    
    if (chargeId) {
      handleSubmit();
      navigate('/admin/dashboard');
    }
  }, []);


  const getComponentProps = (view: OnboardingView) => {
    const baseProps = {};
    switch (view?.label) {
      case "ServiceSelection":
        return {
          ...baseProps,
          selectedPlaces,
          setSelectedPlaces,
        };
      case "SizeSelection":
        return {
          ...baseProps,
          selected: selectedSize,
          setSelected: setSelectedSize,
        };
      case "ProductEntryMethodSelection":
        return {
          ...baseProps,
          selectedProviders,
          setSelectedProviders,
        };
      default:
        return baseProps;
    }
  };

  const renderCurrentView = () => {
    const view = onboardingConfig.views[currentStep];
    const Component = view?.component;
    return <Component {...getComponentProps(view)} />;
  };

  const handleNext = () => {
    analytics.sendOnboardingStepCompletedEvent({
      step: currentStep,
      stepName: onboardingConfig.views[currentStep].label,
      email: user.email ?? user.id,
    });
    setDirection("forward");
    setTimeout(() => setCurrentStep(currentStep + 1), 5);
    setTimeout(() => {
      setCurrentWidth(
        `${onboardingConfig.views[currentStep + 1].windowWidth}px`
      );
    }, widthChangeDelay);
  };

  const handlePrev = () => {
    setDirection("backward");
    setTimeout(() => setCurrentStep(currentStep - 1), 5);
    setTimeout(() => {
      setCurrentWidth(
        `${onboardingConfig.views[currentStep - 1].windowWidth}px`
      );
    }, widthChangeDelay);
  };

  const handleSubmit = async () => {
    analytics.sendOnboardingFinishedEvent({
      completedSteps: currentStep,
      email: user.email ?? user.id,
    })
    analytics.sendPlanSelectionSkippedEvent({
      email: user.email ?? user.id,
    })
    await initializeUserData({
      didCompleteOnboarding: true,
      didSkipOnboarding: false,
      onboardingPlaces: selectedPlaces,
      onboardingSize: selectedSize,
      onboardingExistingProvider: selectedProviders,
    });
  };

  const navButtonStyles = {
    base: "flex items-center gap-2 px-4 py-2.5 rounded-md font-medium transition-colors text-sm sm:text-base min-w-[100px] justify-center disabled:opacity-50",
    primary:
      "bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
    secondary:
      "bg-white border border-slate-200 text-slate-800 hover:bg-slate-50 hover:text-slate-900 focus:ring-2 focus:ring-slate-500 focus:ring-offset-2",
    ghost: "text-slate-600 hover:text-slate-900 hover:bg-slate-100",
  };

  const handleSkipOnboarding = async () => {
    analytics.sendOnboardingFinishedEvent({
      completedSteps: currentStep,
      email: user.email ?? user.id,
    })
    await initializeUserData({
      didCompleteOnboarding: false,
      didSkipOnboarding: true,
      onboardingPlaces: selectedPlaces,
      onboardingSize: selectedSize,
      onboardingExistingProvider: selectedProviders,
    });
  };

  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (onboardingConfig.views[currentStep]?.canEndOnboarding) {
      timeout = setTimeout(() => {
        setCanEndOnboarding(
          onboardingConfig.views[currentStep].canEndOnboarding
        );
      }, isMobile ? 0 :1200);
    } else {
      setCanEndOnboarding(onboardingConfig.views[currentStep].canEndOnboarding);
    }
    return () => clearTimeout(timeout);
  }, [currentStep]);

  useEffect(() => {
    const timer = setTimeout(() => {
      updateCurrentUser({ didStartOnboarding: true });
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (user.didCompleteOnboarding || user.didSkipOnboarding) {
      navigate("/admin/dashboard");
    } else {
      setShouldRenderOnboarding(true);
    }
  }, [user]);

  useEffect(() => {
    if (currentStep === 0) {
      setTimeout(() => {
        handleNext();
      }, 1500);
    }
  }, [currentStep]);

  return (
    <div className="bg-gradient-to-r from-pink-100 via-blue-100 to-green-100 h-screen flex justify-center md:items-center px-[0px] md:px-8 overflow-x-hidden">

      <Fade in={shouldRenderOnboarding}>
        <div
          style={{ width: currentWidth }}
          className="max-w-full h-full absolute md:static mx-auto flex flex-col items-center justify-center"
        >
          <SwitchTransition>
            <CSSTransition
              key={currentStep}
              timeout={!isMobile ? 300 : 0}
              classNames={
                direction === "forward" ? "fade-forward" : "fade-backward"
              }
            >
              <>
                {onboardingConfig.views[currentStep]?.label !== "Welcome" ? (
                  <div className="card max-h-[100vh] h-full md:h-fit flex flex-col overflow-scroll no-scrollbar md:max-h-unset p-6 md:p-[40px] rounded-lg shadow-lg w-full bg-white">
                    <h1 className="text-[20px] font-semibold">
                      {onboardingConfig.views[currentStep]?.title}
                    </h1>
                    <p className="text-[13px] text-gray-600">
                      {onboardingConfig.views[currentStep]?.description}
                    </p>
                    <div
                      className={`my-5 border-t py-[20px] pb-[34px] md:py-0 border-b md:border-0  md:overflow-y-scroll no-scrollbar ${
                        onboardingConfig.views[currentStep]?.containerClasses ||
                        ""
                      }`}
                    >
                      {renderCurrentView()}
                    </div>

                    <nav
                    className="absolute md:hidden bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg p-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                      {onboardingConfig.views[currentStep]?.prev && (
                        <button
                        className={`${navButtonStyles.base} ${navButtonStyles.secondary}`}
                        onClick={handlePrev}
                        >
                        <ChevronLeftIcon className="h-4 w-4" />
                        <span>{t('common.previous')}</span>
                        </button>
                      )}

        
                        { canEndOnboarding && (
                        <button
                          className={`${navButtonStyles.base} ${navButtonStyles.ghost}`}
                          onClick={handleSkipOnboarding}
                        >
                          <span>{t('onboarding.skip_for_now')}</span>
                        </button>
                        )}

{onboardingConfig.views[currentStep]?.skippable && (
                          <button
                            className={`${navButtonStyles.base} ${navButtonStyles.ghost}`}
                            onClick={handleSubmit}
                          >
                            <span>{t('onboarding.skip_for_now')}</span>
                          </button>
                        )}

                        {!onboardingConfig.views[currentStep]?.isLast && !onboardingConfig.views[currentStep]?.skippable && (
                        <button
                          className={`${navButtonStyles.base} ${navButtonStyles.primary}`}
                          onClick={handleNext}
                          disabled={
                          onboardingConfig.views[currentStep]?.label === "ServiceSelection" &&
                          selectedPlaces.length === 0
                          }
                        >
                          <span>{t('common.next')}</span>
                          <ChevronRightIcon className="h-4 w-4" />
                        </button>
                        )}

                        {onboardingConfig.views[currentStep]?.isLast && (
                        <button
                          className={`${navButtonStyles.base} ${navButtonStyles.primary}`}
                          onClick={handleSubmit}
                        >
                          <span>{t('onboarding.lets_start')}</span>
                          <ChevronRightIcon className="h-4 w-4" />
                        </button>
                        )}
          
                      </div>
                    </nav>

                    <nav className="hidden md:flex justify-between items-center mt-6 gap-3 sm:gap-4">
                      {onboardingConfig.views[currentStep]?.prev && (
                        <button
                          className={`${navButtonStyles.base} ${navButtonStyles.secondary}`}
                          onClick={handlePrev}
                        >
                          <ChevronLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                          <span>{t('common.previous')}</span>
                        </button>
                      )}

                      <div className="gap-3 hidden md:flex sm:gap-4 ml-auto">
                        {onboardingConfig.views[currentStep]?.skippable && (
                          <button
                            className={`${navButtonStyles.base} ${navButtonStyles.ghost}`}
                            onClick={handleSubmit}
                          >
                            <span>{t('onboarding.skip_for_now')}</span>
                          </button>
                        )}

                        {!onboardingConfig.views[currentStep]?.isLast &&
                          onboardingConfig.views[currentStep]?.next && (
                            <button
                              className={`${navButtonStyles.base} ${
                                selectedPlaces.length > 0
                                  ? navButtonStyles.primary
                                  : navButtonStyles.secondary
                              }`}
                              onClick={handleNext}
                              disabled={
                                onboardingConfig.views[currentStep]?.label ===
                                  "ServiceSelection" &&
                                selectedPlaces.length === 0
                              }
                            >
                              <span>{t('common.next')}</span>
                              <ChevronRightIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>
                          )}

                        {onboardingConfig.views[currentStep]?.isLast && (
                          <button
                            className={`${navButtonStyles.base} ${navButtonStyles.primary}`}
                            onClick={handleSubmit}
                          >
                            <span>{t('onboarding.lets_start')}</span>
                            <ChevronRightIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                          </button>
                        )}
                      </div>
                    </nav>
                  </div>
                ) : (
                  <WelcomeView />
                )}
              </>
            </CSSTransition>
          </SwitchTransition>

          <Fade in={canEndOnboarding}>
            <button
              data-testid="skip-onboarding"
              onClick={handleSkipOnboarding}
              className="hidden md:block self-start mt-5 font-[500] text-[12px] text-slate-600 hover:text-slate-900"
            >
              {t('onboarding.dont_want_help')}
              <ArrowForwardIcon fontSize="small" sx={{ marginLeft: "4px" }} />
            </button>
          </Fade>
        </div>
      </Fade>
    </div>
  );
};

export default OnboardingPage;
